import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FileDisplay from "../../components/FileDisplay";
import "./Invitation.css";
import { isInvitationNavigationEnabled } from "../../Utils/invitationNavigation";
import { useParams, useSearchParams } from "react-router-dom";
import RsvpAdditionalOptions from "../../components/RsvpAdditionalOptions";
import {
  createRsvpOperationId,
  loadPublicInvitation,
  submitPublicRsvp,
} from "../../Utils/publicInvitationApi";
import {
  createEmptyRsvpDetails,
  finalizeRsvpDetails,
  getAttendeeExactDish,
  hasEnabledRsvpOptions,
  normalizeRsvpDetails,
  normalizeRsvpOptions,
  resizeRsvpDetails,
  validateRsvpDetails,
} from "../../Utils/rsvpOptions";

const normalizePhone = (value) => String(value || "").replace(/\D/g, "");

const getFittedInvitationSize = (naturalWidth, naturalHeight) => {
  if (!naturalWidth || !naturalHeight) return null;

  const maxWidth = Math.min(window.innerWidth * 0.8, window.innerWidth - 48);
  const maxHeight = Math.min(window.innerHeight * 0.8, window.innerHeight - 48);
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);

  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  };
};

const scrollToMissingMobileField = (element) => {
  if (!element || !window.matchMedia("(max-width: 575px)").matches) return;
  window.requestAnimationFrame(() => {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => element.focus({ preventScroll: true }), 350);
  });
};

const getPartySize = (contact) => {
  const parsed = parseInt(String(contact?.howMany || "1"), 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const RSVP_STATUS_LABELS = {
  approved: "אגיע בשמחה",
  pending: "עדיין לא בטוח",
  notComing: "לצערי לא אגיע",
};

const RSVP_THANK_YOU_SUMMARY_KEY = "rsvpThankYouSummary";

const getRsvpSelectionSummary = (status, partySize, detailsValue, optionsValue) => {
  const summary = {
    status,
    statusLabel: RSVP_STATUS_LABELS[status] || "",
    partySize: status === "approved" ? partySize : null,
    dishes: [],
    gender: null,
    transportation: null,
  };
  if (status !== "approved") return summary;

  const options = normalizeRsvpOptions(optionsValue);
  const details = normalizeRsvpDetails(detailsValue, partySize);

  if (options.childMeal.enabled || ["vegetarian", "vegan", "glattKosher", "glutenFree"].some((key) => options[key].enabled)) {
    const dishCounts = new Map();
    details.attendees.forEach((attendee) => {
      const label = getAttendeeExactDish(attendee, attendee, options).label;
      if (label) dishCounts.set(label, (dishCounts.get(label) || 0) + 1);
    });
    summary.dishes = Array.from(dishCounts, ([label, count]) => ({ label, count }));
  }

  if (options.gender.enabled) {
    summary.gender = {
      male: details.attendees.filter((attendee) => attendee.gender === "male").length,
      female: details.attendees.filter((attendee) => attendee.gender === "female").length,
    };
  }

  if (options.transportation.enabled) {
    summary.transportation = details.transportation.needsRide
      ? { needed: true, seats: details.transportation.seats }
      : { needed: false, seats: 0 };
  }

  return summary;
};

const storeRsvpThankYouSummary = (status, partySize, details, options) => {
  try {
    window.sessionStorage.setItem(
      RSVP_THANK_YOU_SUMMARY_KEY,
      JSON.stringify(getRsvpSelectionSummary(status, partySize, details, options))
    );
  } catch (error) {
    console.warn("Unable to store RSVP confirmation summary", error);
  }
};

const normalizeGuestRsvpDetails = (value, partySize, rsvpOptions) => {
  const details = normalizeRsvpDetails(value, partySize);
  const options = normalizeRsvpOptions(rsvpOptions);
  if (!options.transportation.enabled || details.transportation.answered) return details;
  return {
    ...details,
    transportation: { answered: true, needsRide: false, seats: 0 },
  };
};

const UnknownClient = ({ addAlertText, id, rsvpOptions }) => {
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pendingExistingUpdate, setPendingExistingUpdate] = useState(null);
  const [recognizedExistingClient, setRecognizedExistingClient] = useState(null);
  const [recognizedAssignedClient, setRecognizedAssignedClient] = useState(null);
  const phoneLookupRequestRef = useRef(0);
  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const [howMany, setHowMany] = useState(1);
  const [rsvpDetails, setRsvpDetails] = useState(() => normalizeGuestRsvpDetails(createEmptyRsvpDetails(1), 1, rsvpOptions));
  const [rsvpValidationAttempt, setRsvpValidationAttempt] = useState(0);
  const [submittingStatus, setSubmittingStatus] = useState("");

  const clearRecognizedClient = () => {
    setRecognizedExistingClient(null);
    setRecognizedAssignedClient(null);
    setHowMany(1);
    setRsvpDetails(normalizeGuestRsvpDetails(createEmptyRsvpDetails(1), 1, rsvpOptions));
  };

  const loadExistingClientByPhone = async (rawPhone) => {
    const normalizedPhone = normalizePhone(rawPhone);
    if (!/^\d{9,15}$/.test(normalizedPhone)) return;
    const requestId = ++phoneLookupRequestRef.current;
    try {
      const response = await loadPublicInvitation({ eventId: id, phoneNumber: normalizedPhone });
      if (requestId !== phoneLookupRequestRef.current) return;
      const existingClient = response.guest
        ? { ...response.guest, phoneNumber: normalizedPhone }
        : null;
      if (!existingClient) {
        if (recognizedExistingClient) clearRecognizedClient();
        return;
      }
      const partySize = getPartySize(existingClient);
      setRecognizedExistingClient(existingClient);
      setRecognizedAssignedClient(existingClient.isAssignedToTable ? existingClient : null);
      setName(existingClient.name || "");
      setHowMany(partySize);
      setRsvpDetails(normalizeGuestRsvpDetails(existingClient.rsvpDetails, partySize, rsvpOptions));
    } catch (error) {
      console.error("Unable to load an existing RSVP by phone", error);
    }
  };

  const changePartySize = (nextSize) => {
    if (nextSize === howMany) return;
    setHowMany(nextSize);
    setRsvpDetails((current) => resizeRsvpDetails(current, nextSize));
  };

  const persistExistingUpdate = async (plan) => {
    setSubmittingStatus(plan.submission.status);
    let result;
    try {
      result = await submitPublicRsvp(plan.submission);
    } catch (error) {
      const conflict = error?.code === "PUBLIC_RSVP_STATE_CONFLICT";
      addAlertText({
        mode: conflict ? "warning" : "danger",
        text: conflict
          ? "נראה שבעלי האירוע עדכנו את הפרטים שלכם בינתיים. רעננו את הדף ונסו שוב 😊"
          : "לא הצלחנו לשמור את אישור ההגעה. נסו שוב בעוד רגע.",
      });
      setSubmittingStatus("");
      return;
    }
    if (result.removedFromTable) {
      storeRsvpThankYouSummary(
        plan.submission.status,
        plan.submission.howMany,
        plan.submission.rsvpDetails,
        rsvpOptions
      );
      addAlertText({ mode: "success", text: "הסטטוס עודכן והוסרת מסידורי ההושבה." });
      setTimeout(() => {
        window.location.href = "/thanks";
      }, 3500);
      return;
    }

    storeRsvpThankYouSummary(
      plan.submission.status,
      plan.submission.howMany,
      plan.submission.rsvpDetails,
      rsvpOptions
    );
    window.location.href = "/thanks";
  };

  const addNewClient = async (e) => {
    if (submittingStatus) return;
    let status = typeof e === "string" ? e : e.currentTarget.dataset.status;
    const normalizedPhone = normalizePhone(phoneNumber);
    const missing = [];
    const firstMissingField = !name.trim()
      ? nameInputRef.current
      : !phoneNumber || !/^\d{9,15}$/.test(normalizedPhone)
        ? phoneInputRef.current
        : null;
    if (!name.trim()) missing.push("שם מלא");
    if (!phoneNumber) missing.push("מספר טלפון");
    else if (!/^\d{9,15}$/.test(normalizedPhone)) missing.push("מספר טלפון תקין");
    if (status === "approved") {
      const optionValidation = validateRsvpDetails(rsvpDetails, rsvpOptions, howMany);
      if (optionValidation) {
        missing.push(optionValidation.replace(/^יש להשלים:\s*/, ""));
        if (!firstMissingField && optionValidation.includes("חלוקת גברים ונשים")) {
          setRsvpValidationAttempt((current) => current + 1);
        }
      }
    }
    if (missing.length > 0) {
      addAlertText({ mode: "warning", text: `יש להשלים לפני השליחה: ${missing.join(" • ")}` });
      scrollToMissingMobileField(firstMissingField);
      return;
    }
    const completedRsvpDetails = status === "approved"
      ? finalizeRsvpDetails(rsvpDetails, rsvpOptions, howMany)
      : undefined;
    setSubmittingStatus(status);

    let lookup;
    try {
      lookup = await loadPublicInvitation({ eventId: id, phoneNumber: normalizedPhone });
    } catch (error) {
      console.error("Unable to confirm the current RSVP state", error);
      addAlertText({ mode: "danger", text: "לא הצלחנו לבדוק את אישור ההגעה. נסו שוב בעוד רגע." });
      setSubmittingStatus("");
      return;
    }
    const existingClient = lookup.guest
      ? { ...lookup.guest, phoneNumber: normalizedPhone }
      : null;

    if (existingClient) {
      const isAssignedToTable = existingClient.isAssignedToTable === true;
      const isRemovingFromTable = isAssignedToTable && status === "notComing";
      const submission = {
        eventId: id,
        name,
        phoneNumber: normalizedPhone,
        status,
        howMany: status === "approved"
          ? (isAssignedToTable ? getPartySize(existingClient) : howMany)
          : getPartySize(existingClient),
        ...(completedRsvpDetails ? { rsvpDetails: completedRsvpDetails } : {}),
        operationId: createRsvpOperationId(),
        expectedStateToken: existingClient.stateToken,
      };
      const plan = { submission };

      if (isAssignedToTable) {
        const alreadyAcknowledged = normalizePhone(recognizedAssignedClient?.phoneNumber) === normalizedPhone;

        if (!isRemovingFromTable && alreadyAcknowledged) {
          await persistExistingUpdate(plan);
          return;
        }

        if (!isRemovingFromTable) {
          setSubmittingStatus("");
          setRecognizedAssignedClient(existingClient);
          setHowMany(getPartySize(existingClient));
          setPendingExistingUpdate({
            type: "assignedInfo",
            title: "כבר הוקצו לכם מקומות",
            message: "הוקצו לכם מקומות ישיבה ולכן לא ניתן לשנות כאן את מספר האורחים. לשינוי, יש לפנות לבעלי האירוע.",
          });
          return;
        }

        setPendingExistingUpdate({
          ...plan,
          type: "removeFromTable",
          title: "שינוי סטטוס ההגעה",
          message: "בעלי האירוע כבר הקצו לך שולחן. לאחר שינוי סטטוס ההגעה, המערכת תסיר אותך אוטומטית מהשולחן. אם יהיה שינוי נוסף בסטטוס שלך, יש לעדכן את בעלי האירוע.",
        });
        setSubmittingStatus("");
        return;
      }

      await persistExistingUpdate(plan);
      return;
    }

    try {
      await submitPublicRsvp({
        eventId: id,
        phoneNumber: normalizedPhone,
        howMany: status === "approved" ? howMany : 1,
        name,
        status,
        rsvpDetails: completedRsvpDetails,
        operationId: createRsvpOperationId(),
        expectedStateToken: "missing",
      });
      storeRsvpThankYouSummary(status, status === "approved" ? howMany : 1, completedRsvpDetails, rsvpOptions);
      window.location.href = "/thanks";
    } catch (error) {
      console.error("Unable to save RSVP", error);
      addAlertText({ mode: "danger", text: "לא הצלחנו לשמור את אישור ההגעה. נסו שוב בעוד רגע." });
      setSubmittingStatus("");
    }

    return;
  };

  return (
    <section className="rsvp-card" id="guest-rsvp" aria-labelledby="rsvp-title">
      <div className="rsvp-card__heading">
        <h2 id="rsvp-title">אנא אשרו הגעתכם לאירוע</h2>
        <p>מלאו את הפרטים ובחרו את התשובה המתאימה.</p>
      </div>

      <div className="rsvp-fields">
        <label className="rsvp-field">
          <span>שם מלא</span>
          <input ref={nameInputRef} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="הקלידו שם מלא" autoComplete="name" />
        </label>
        <label className="rsvp-field">
          <span>מספר טלפון</span>
          <input
            ref={phoneInputRef}
            type="tel"
            value={phoneNumber}
            onChange={(e) => {
              phoneLookupRequestRef.current += 1;
              setPhoneNumber(e.target.value);
              if (normalizePhone(e.target.value) !== normalizePhone(recognizedExistingClient?.phoneNumber)) {
                if (recognizedExistingClient) {
                  setName("");
                  clearRecognizedClient();
                } else {
                  setRecognizedAssignedClient(null);
                }
              }
            }}
            onBlur={(e) => loadExistingClientByPhone(e.currentTarget.value)}
            placeholder="הקלידו מספר טלפון"
            autoComplete="tel"
            inputMode="tel"
          />
        </label>
      </div>

      <PartySizeControl
        value={howMany}
        disabled={!!recognizedAssignedClient}
        onIncrease={() => changePartySize(howMany + 1)}
        onDecrease={() => howMany > 1 && changePartySize(howMany - 1)}
      />

      {recognizedAssignedClient && (
        <div className="seating-note" role="note">
          <span aria-hidden="true">!</span>
          <p>הוקצו לכם מקומות ישיבה ולכן לא ניתן לשנות כאן את מספר האורחים. לשינוי, יש לפנות לבעלי האירוע.</p>
        </div>
      )}

      {hasEnabledRsvpOptions(rsvpOptions) && (
        <RsvpAdditionalOptions
          options={rsvpOptions}
          partySize={howMany}
          value={rsvpDetails}
          onChange={setRsvpDetails}
          regularMealIsDefault
          aggregateAttendees
          alwaysShowFood
          validationAttempt={rsvpValidationAttempt}
        />
      )}

      <RsvpActions onSelect={addNewClient} submittingStatus={submittingStatus} />

      {pendingExistingUpdate && createPortal(
        <div className="rsvp-modal-backdrop" role="presentation">
          <div className="rsvp-modal" role="dialog" aria-modal="true" aria-labelledby="existing-guest-confirmation-title">
            <button type="button" className="rsvp-modal__close" onClick={() => setPendingExistingUpdate(null)} aria-label="סגירת החלון">×</button>
            <div className="rsvp-modal__icon" aria-hidden="true">!</div>
            <h3 id="existing-guest-confirmation-title">{pendingExistingUpdate.title}</h3>
            <p>{pendingExistingUpdate.message}</p>
            <div className={`rsvp-modal__actions ${pendingExistingUpdate.type === "assignedInfo" ? "rsvp-modal__actions--single" : ""}`}>
              {pendingExistingUpdate.type === "assignedInfo" ? (
                <button type="button" className="rsvp-modal__confirm" onClick={() => setPendingExistingUpdate(null)}>אישור</button>
              ) : (
                <>
                  <button
                    type="button"
                    className="rsvp-modal__confirm"
                    onClick={() => {
                      const plan = pendingExistingUpdate;
                      setPendingExistingUpdate(null);
                      persistExistingUpdate(plan);
                    }}
                  >
                    אישור השינוי
                  </button>
                  <button type="button" className="rsvp-modal__cancel" onClick={() => setPendingExistingUpdate(null)}>ביטול וחזרה</button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

const PartySizeControl = ({ value, onIncrease, onDecrease, disabled = false }) => (
  <div className={`party-size ${disabled ? "party-size--disabled" : ""}`}>
    <div>
      <strong>כמה אנשים תגיעו סה״כ?</strong>
      <span>כולל אתכם</span>
    </div>
    <div className="party-size__stepper" aria-label="כמות אורחים">
      <button type="button" onClick={onIncrease} disabled={disabled} aria-label="הוספת אורח">+</button>
      <output aria-live="polite">{value}</output>
      <button type="button" onClick={onDecrease} disabled={disabled || value <= 1} aria-label="הפחתת אורח">−</button>
    </div>
  </div>
);

const RsvpActions = ({ onSelect, submittingStatus = "" }) => (
  <div className="rsvp-actions" aria-label="בחירת סטטוס הגעה">
    <button type="button" onClick={onSelect} data-status="approved" disabled={Boolean(submittingStatus)} aria-busy={submittingStatus === "approved"} className="rsvp-action rsvp-action--yes">
      <span className={submittingStatus === "approved" ? "rsvp-action__spinner" : ""} aria-hidden="true">{submittingStatus === "approved" ? "" : "✓"}</span>
      אגיע בשמחה
    </button>
    <button type="button" onClick={onSelect} data-status="pending" disabled={Boolean(submittingStatus)} aria-busy={submittingStatus === "pending"} className="rsvp-action rsvp-action--maybe">
      <span className={submittingStatus === "pending" ? "rsvp-action__spinner" : ""} aria-hidden="true">{submittingStatus === "pending" ? "" : "?"}</span>
      עדיין לא בטוח
    </button>
    <button type="button" onClick={onSelect} data-status="notComing" disabled={Boolean(submittingStatus)} aria-busy={submittingStatus === "notComing"} className="rsvp-action rsvp-action--no">
      <span className={submittingStatus === "notComing" ? "rsvp-action__spinner" : ""} aria-hidden="true">{submittingStatus === "notComing" ? "" : "×"}</span>
      לצערי לא אגיע
    </button>
  </div>
);

const confettiPieces = [
  [5, 0.2, 5.8, "#d8b86a"], [12, 1.7, 6.4, "#f3e3b4"], [19, 3.1, 5.5, "#b9d2cb"],
  [27, 0.8, 7.1, "#d7a8a1"], [34, 4.2, 6.2, "#e5c77f"], [41, 2.4, 5.9, "#f5eee1"],
  [48, 5.1, 7.4, "#a9c6bd"], [55, 1.1, 6.7, "#dfb6ad"], [62, 3.7, 5.6, "#d6b260"],
  [69, 0.4, 6.1, "#f2dfac"], [76, 4.8, 7.2, "#b5cec6"], [83, 2.1, 5.7, "#e1aaa2"],
  [91, 3.3, 6.6, "#d8bc73"], [9, 5.6, 7.5, "#f6ead0"], [31, 6.2, 6.9, "#adc8c0"],
  [52, 6.8, 7.7, "#dfb0a8"], [73, 5.9, 6.3, "#e4c982"], [95, 6.5, 7.3, "#f5e6bd"],
];

const InvitationConfetti = () => (
  <div className="invitation-confetti" aria-hidden="true">
    {confettiPieces.map(([x, delay, duration, color], index) => (
      <i
        key={`${x}-${delay}`}
        className={index % 3 === 0 ? "invitation-confetti__piece invitation-confetti__piece--round" : "invitation-confetti__piece"}
        style={{ "--confetti-x": `${x}%`, "--confetti-delay": `${delay}s`, "--confetti-duration": `${duration}s`, "--confetti-color": color }}
      />
    ))}
  </div>
);

const MobileRsvpCta = () => {
  const [isVisible, setIsVisible] = useState(true);
  const hasScrolled = useRef(false);
  const isScrollingToRsvp = useRef(false);
  const scrollTimer = useRef(null);

  useEffect(() => {
    const rsvpSection = document.getElementById("guest-rsvp");
    if (!rsvpSection) return undefined;

    const updateVisibility = () => {
      if (isScrollingToRsvp.current) {
        setIsVisible(false);
        return;
      }
      if (!hasScrolled.current) {
        setIsVisible(true);
        return;
      }
      const topZone = Math.min(240, window.innerHeight * 0.3);
      if (window.scrollY <= topZone) {
        setIsVisible(true);
        return;
      }
      const rsvpTop = rsvpSection.getBoundingClientRect().top;
      setIsVisible(rsvpTop > window.innerHeight * 0.65);
    };

    const handleScroll = () => {
      hasScrolled.current = true;
      updateVisibility();
    };

    updateVisibility();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateVisibility);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateVisibility) : null;
    resizeObserver?.observe(rsvpSection);
    const invitationLayout = document.querySelector(".invitation-layout");
    if (invitationLayout) resizeObserver?.observe(invitationLayout);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateVisibility);
      resizeObserver?.disconnect();
      if (scrollTimer.current) window.clearTimeout(scrollTimer.current);
    };
  }, []);

  const scrollToRsvp = (event) => {
    event.preventDefault();
    hasScrolled.current = true;
    isScrollingToRsvp.current = true;
    setIsVisible(false);
    document.getElementById("guest-rsvp")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (scrollTimer.current) window.clearTimeout(scrollTimer.current);
    scrollTimer.current = window.setTimeout(() => {
      isScrollingToRsvp.current = false;
    }, 900);
  };

  return (
    <a
      className={`mobile-rsvp-cta ${isVisible ? "" : "mobile-rsvp-cta--hidden"}`}
      href="#guest-rsvp"
      onClick={scrollToRsvp}
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
    >
      <span>לאישור הגעה ללחוץ כאן</span>
      <b aria-hidden="true">↓</b>
    </a>
  );
};

const KnownClient = ({ currentClient, id, addAlertText, clientName, rsvpOptions }) => {
  const isAssignedToTable = currentClient.isAssignedToTable === true;
  const savedPartySize = getPartySize(currentClient);
  const [howMany, setHowMany] = useState(savedPartySize);
  const [showSeatingConfirmation, setShowSeatingConfirmation] = useState(false);
  const [rsvpDetails, setRsvpDetails] = useState(() => normalizeGuestRsvpDetails(currentClient.rsvpDetails, savedPartySize, rsvpOptions));
  const [rsvpValidationAttempt, setRsvpValidationAttempt] = useState(0);
  const [submittingStatus, setSubmittingStatus] = useState("");

  const changePartySize = (nextSize) => {
    if (nextSize === howMany) return;
    setHowMany(nextSize);
    setRsvpDetails((current) => resizeRsvpDetails(current, nextSize));
  };

  const updateClient = async (status) => {
    if (submittingStatus) return;
    if (!status) {
      addAlertText({ mode: "warning", text: "יש לבחור סטאטוס הגעה לאירוע." });
      return;
    }
    const currentPartySize = isAssignedToTable ? savedPartySize : howMany;
    const validationError = status === "approved" ? validateRsvpDetails(rsvpDetails, rsvpOptions, currentPartySize) : "";
    if (validationError) {
      if (validationError.includes("חלוקת גברים ונשים")) setRsvpValidationAttempt((current) => current + 1);
      addAlertText({ mode: "warning", text: validationError });
      return;
    }
    setSubmittingStatus(status);
    const submittedRsvpDetails = status === "approved"
      ? finalizeRsvpDetails(rsvpDetails, rsvpOptions, currentPartySize)
      : undefined;
    let result;
    try {
      result = await submitPublicRsvp({
        eventId: id,
        name: currentClient.name || "",
        phoneNumber: currentClient.phoneNumber || "",
        status,
        howMany: status === "approved"
          ? (isAssignedToTable ? savedPartySize : howMany)
          : savedPartySize,
        ...(submittedRsvpDetails ? { rsvpDetails: submittedRsvpDetails } : {}),
        operationId: createRsvpOperationId(),
        expectedStateToken: currentClient.stateToken,
      });
    } catch (error) {
      console.error("Unable to save RSVP", error);
      addAlertText({
        mode: error?.code === "PUBLIC_RSVP_STATE_CONFLICT" ? "warning" : "danger",
        text: error?.code === "PUBLIC_RSVP_STATE_CONFLICT"
          ? "נראה שבעלי האירוע עדכנו את הפרטים שלכם בינתיים. רעננו את הדף ונסו שוב 😊"
          : "לא הצלחנו לשמור את אישור ההגעה. נסו שוב בעוד רגע.",
      });
      setSubmittingStatus("");
      return;
    }

    storeRsvpThankYouSummary(status, currentPartySize, submittedRsvpDetails, rsvpOptions);
    if (result.removedFromTable) {
      addAlertText({
        mode: "success",
        text: "הסטטוס עודכן והוסרת מסידורי ההושבה.",
      });
      setTimeout(() => {
        window.location.href = "/thanks";
      }, 3500);
      return;
    }

    window.location.href = "/thanks";
  };

  const handleStatusSelection = (event) => {
    const status = event.currentTarget.dataset.status;
    if (status === "notComing" && isAssignedToTable) {
      setShowSeatingConfirmation(true);
      return;
    }
    updateClient(status);
  };

  const confirmNotComing = () => {
    setShowSeatingConfirmation(false);
    updateClient("notComing");
  };

  return (
    <section className="rsvp-card" id="guest-rsvp" aria-labelledby="rsvp-title">
      <div className="rsvp-card__heading">
        <h2 id="rsvp-title">שלום {clientName}</h2>
        <p>אנא אשרו הגעתכם לאירוע</p>
      </div>

      <PartySizeControl
        value={howMany}
        disabled={isAssignedToTable}
        onIncrease={() => !isAssignedToTable && changePartySize(howMany + 1)}
        onDecrease={() => !isAssignedToTable && howMany > 1 && changePartySize(howMany - 1)}
      />

      {isAssignedToTable && (
        <div className="seating-note" role="note">
          <span aria-hidden="true">!</span>
          <p>הוקצו לכם מקומות ישיבה ולכן לא ניתן לשנות כאן את מספר האורחים. לשינוי, יש לפנות לבעלי האירוע.</p>
        </div>
      )}

      {hasEnabledRsvpOptions(rsvpOptions) && (
        <RsvpAdditionalOptions
          options={rsvpOptions}
          partySize={isAssignedToTable ? savedPartySize : howMany}
          value={rsvpDetails}
          onChange={setRsvpDetails}
          regularMealIsDefault
          aggregateAttendees
          alwaysShowFood
          validationAttempt={rsvpValidationAttempt}
        />
      )}

      <RsvpActions onSelect={handleStatusSelection} submittingStatus={submittingStatus} />

      {showSeatingConfirmation && createPortal(
        <div className="rsvp-modal-backdrop" role="presentation">
          <div className="rsvp-modal" role="dialog" aria-modal="true" aria-labelledby="seating-confirmation-title">
            <button type="button" className="rsvp-modal__close" onClick={() => setShowSeatingConfirmation(false)} aria-label="סגירת החלון">×</button>
            <div className="rsvp-modal__icon" aria-hidden="true">!</div>
            <h3 id="seating-confirmation-title">שינוי סטטוס ההגעה</h3>
            <p>
              בעלי האירוע כבר הקצו לך שולחן. לאחר שינוי סטטוס ההגעה, המערכת תסיר אותך אוטומטית מהשולחן. אם יהיה שינוי נוסף בסטטוס שלך, יש לעדכן את בעלי האירוע.
            </p>
            <div className="rsvp-modal__actions">
              <button type="button" className="rsvp-modal__confirm" onClick={confirmNotComing}>אישור השינוי</button>
              <button type="button" className="rsvp-modal__cancel" onClick={() => setShowSeatingConfirmation(false)}>ביטול וחזרה</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

const Invitation = ({ addAlertText }) => {
  const { id, phone } = useParams();
  const [searchParams] = useSearchParams();
  const invitationOnly = searchParams.get("view") === "save-the-date";
  // The two Dashboard share links have no phone; the manager's own preview is not a view.
  const shareLinkType = phone || searchParams.get("preview") ? "" : (invitationOnly ? "invite" : "rsvp");
  const countedViewRef = useRef("");
  const [namesColor, setNamesColor] = useState("#D3AF37");
  const [quouteFontSize, setQuouteFontSize] = useState("28");
  const [quouteColor, setQuouteColor] = useState("#D3AF37");
  const [parentsFontSize, setParentsFontSize] = useState("24");
  const [parentsColor, setParentsColor] = useState("#D3AF37");
  const [backgroundColor, setBackgroundColor] = useState("#FFFEFC");
  const [borderColor, setBorderColor] = useState("#263F3F");
  const [eventOwners, seteventOwners] = useState("");
  const [dedicationText, setDedicationText] = useState("");
  const [date, setDate] = useState("");
  const [day, setDay] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [locationName, setLocationName] = useState("");
  const [brideParentsLabel, setBrideParentsLabel] = useState("");
  const [groomParentsLabel, setGroomParentsLabel] = useState("");
  const [brideParentsNames, setBrideParentsNames] = useState("");
  const [groomParentsNames, setGroomParentsNames] = useState("");
  const [dedicationSecond, setdedicationSecond] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentType, setAttachmentType] = useState("");
  const [attachmentPreparing, setAttachmentPreparing] = useState(false);
  const [previewCheck, setPreviewCheck] = useState(0);
  const [withWazeLink, setWithWazeLink] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [rsvpOptions, setRsvpOptions] = useState(() => normalizeRsvpOptions());
  const [canMagnifyInvitation, setCanMagnifyInvitation] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 561px) and (hover: hover) and (pointer: fine)").matches
  );
  const [isInvitationExpanded, setIsInvitationExpanded] = useState(false);
  const [isInvitationClosing, setIsInvitationClosing] = useState(false);
  const [expandedInvitationSize, setExpandedInvitationSize] = useState(null);
  const invitationNaturalSizeRef = useRef(null);

  const [isKnown, setIsKnown] = useState(false);
  const [currentClient, setCurrentClient] = useState({});
  const [isOn, setisOn] = useState(false);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 561px) and (hover: hover) and (pointer: fine)");
    const syncDesktopState = (event) => {
      setCanMagnifyInvitation(event.matches);
      if (!event.matches) {
        setIsInvitationExpanded(false);
        setIsInvitationClosing(false);
      }
    };

    desktopQuery.addEventListener("change", syncDesktopState);
    return () => desktopQuery.removeEventListener("change", syncDesktopState);
  }, []);

  useEffect(() => {
    if (!isInvitationExpanded) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsInvitationClosing(true);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isInvitationExpanded]);

  useEffect(() => {
    if (!isInvitationExpanded) return undefined;

    const resizeExpandedInvitation = () => {
      const naturalSize = invitationNaturalSizeRef.current;
      if (!naturalSize) return;
      setExpandedInvitationSize(getFittedInvitationSize(naturalSize.width, naturalSize.height));
    };

    window.addEventListener("resize", resizeExpandedInvitation);
    return () => window.removeEventListener("resize", resizeExpandedInvitation);
  }, [isInvitationExpanded]);

  useEffect(() => {
    const fireAsync = async () => {
      try {
        // Count each page open once, even if this effect re-runs during the same visit.
        const viewKey = shareLinkType ? `${id}:${shareLinkType}` : "";
        const countView = viewKey && countedViewRef.current !== viewKey ? shareLinkType : undefined;
        if (countView) countedViewRef.current = viewKey;
        const response = await loadPublicInvitation({ eventId: id, phoneNumber: invitationOnly ? undefined : phone, ...(countView ? { countView } : {}) });
        const invitationData = response.event || {};
        setisOn(true);
        setNamesColor(invitationData.namesColor);
        setQuouteFontSize(invitationData.quouteFontSize);
        setQuouteColor(invitationData.quouteColor);
        setParentsFontSize(invitationData.parentsFontSize);
        setParentsColor(invitationData.parentsColor);
        setBackgroundColor(invitationData.backgroundColor);
        setBorderColor(invitationData.borderColor);
        seteventOwners(invitationData.displayEventName || "");
        setDedicationText(invitationData.dedicationText);
        setDate(invitationData.date);
        setDay(invitationData.day);
        setLocationAddress(invitationData.locationAddress);
        setLocationName(invitationData.locationName);
        setBrideParentsLabel(invitationData.brideParentsLabel);
        setGroomParentsLabel(invitationData.groomParentsLabel);
        setBrideParentsNames(invitationData.brideParentsNames);
        setGroomParentsNames(invitationData.groomParentsNames);
        setdedicationSecond(invitationData.dedicationSecond);
        setWithWazeLink(isInvitationNavigationEnabled(invitationData));
        setShowConfetti(invitationData.showConfetti !== false);
        setRsvpOptions(normalizeRsvpOptions(invitationData.additionalRsvpOptions));

        if (invitationData.attachment) {
          setAttachmentUrl(invitationData.attachment.url || "");
          setAttachmentType(invitationData.attachment.fileType || "");
        }
        setAttachmentPreparing(invitationData.attachment?.preparing === true);

        const normalizedRoutePhone = normalizePhone(phone);
        if (response.guest) {
          setIsKnown(true);
          setCurrentClient({ ...response.guest, phoneNumber: normalizedRoutePhone });
        } else {
          setIsKnown(false);
          setCurrentClient({});
        }
        return response;
      } catch (error) {
        console.error("Unable to load public invitation", error);
        addAlertText({ mode: "danger", text: "לא הצלחנו לטעון את ההזמנה. נסו שוב בעוד רגע." });
        throw error;
      }
    };
    if (id) {
      fireAsync().catch(() => {});
    }
  }, [addAlertText, id, phone, invitationOnly, shareLinkType]);

  // A FREE invitation is shown only once its watermark is ready; until then, check again.
  useEffect(() => {
    if (!attachmentPreparing || !id) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const response = await loadPublicInvitation({ eventId: id, phoneNumber: invitationOnly ? undefined : phone });
        const attachment = response.event?.attachment;
        if (!active) return;
        if (attachment?.url) {
          setAttachmentUrl(attachment.url);
          setAttachmentType(attachment.fileType || "");
          setAttachmentPreparing(false);
        } else if (attachment?.preparing === true) {
          setPreviewCheck((count) => count + 1);
        } else {
          setAttachmentPreparing(false);
        }
      } catch (_error) {
        if (active) setPreviewCheck((count) => count + 1);
      }
    }, 5000);
    return () => { active = false; clearTimeout(timer); };
  }, [attachmentPreparing, previewCheck, id, phone, invitationOnly]);
  if (!isOn) {
    return (
      <div className="invitation-loading" role="status" aria-label="ההזמנה נטענת">
        <div className="invitation-loading__spinner">
          <span className="visually-hidden">ההזמנה נטענת</span>
        </div>
      </div>
    );
  }
  if (!id) {
    window.location.href = "/";
    return;
  }
  return (
    <main
      className={`guest-invitation${invitationOnly ? " guest-invitation--save-date" : ""}`}
      dir="rtl"
      style={{
        "--event-background": backgroundColor || "#f7f3ed",
        "--event-border": borderColor || "#263f3f",
        "--event-accent": namesColor || "#b68b45",
      }}
    >
      <div className="guest-invitation__glow guest-invitation__glow--one" aria-hidden="true" />
      <div className="guest-invitation__glow guest-invitation__glow--two" aria-hidden="true" />

      <div className="invitation-layout">
        <section className="invitation-visual" aria-label="ההזמנה לאירוע">
          {showConfetti && <InvitationConfetti />}
          <div className="invitation-media">
            {attachmentUrl ? (
              String(attachmentType).toLowerCase().includes("image") ? (
                canMagnifyInvitation ? (
                  <button
                    type="button"
                    className="invitation-media__zoom-button"
                    onClick={(event) => {
                      const image = event.currentTarget.querySelector("img");
                      if (image?.naturalWidth && image?.naturalHeight) {
                        invitationNaturalSizeRef.current = {
                          width: image.naturalWidth,
                          height: image.naturalHeight,
                        };
                        setExpandedInvitationSize(getFittedInvitationSize(image.naturalWidth, image.naturalHeight));
                      }
                      setIsInvitationClosing(false);
                      setIsInvitationExpanded(true);
                    }}
                    aria-label={`הגדלת תמונת ההזמנה${eventOwners ? `: ${eventOwners}` : ""}`}
                  >
                    <img src={attachmentUrl} className="previewActive" alt={eventOwners || "הזמנה לאירוע"} />
                  </button>
                ) : (
                  <img src={attachmentUrl} className="previewActive" alt={eventOwners || "הזמנה לאירוע"} />
                )
              ) : (
                <FileDisplay source={attachmentUrl} localState={attachmentType} setSource={setAttachmentUrl} />
              )
            ) : attachmentPreparing ? (
              <div className="invitation-media__placeholder" role="status">מכין את ההזמנה לתצוגה - נא להמתין</div>
            ) : (
              <div className="invitation-media__placeholder" aria-hidden="true">{eventOwners}</div>
            )}
          </div>
          {(eventOwners || date || day || locationName || locationAddress) && (
            <div className="invitation-event-summary">
              {attachmentUrl && eventOwners && <h1 className="invitation-event-title">{eventOwners}</h1>}
              {(date || day || locationName || locationAddress) && (
                <div className="invitation-event-details" aria-label="פרטי האירוע">
                  {(date || day) && (
                    <div className="invitation-event-details__row">
                      {date && <span>בתאריך <strong>{date}</strong></span>}
                      {day && <span>{day}</span>}
                    </div>
                  )}
                  {(locationName || locationAddress) && (
                    <div className="invitation-event-details__row invitation-event-details__row--location">
                      {locationName && <strong>{locationName}</strong>}
                      {locationAddress && <span>{locationAddress}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        <div className="invitation-content">
          {withWazeLink && (
            <a className="waze-button" href={`https://waze.to/?q=${locationAddress}&navigate=yes`} target="_blank" rel="noopener noreferrer">
              <img src="/assets/icons/waze.svg" alt="" />
              <span><strong>ניווט לאירוע</strong><small>פתיחה ב־Waze</small></span>
              <b aria-hidden="true">←</b>
            </a>
          )}

          {!invitationOnly && (isKnown ? (
            <KnownClient addAlertText={addAlertText} currentClient={currentClient} clientName={currentClient?.name || ""} id={id} rsvpOptions={rsvpOptions} />
          ) : (
            <UnknownClient addAlertText={addAlertText} id={id} rsvpOptions={rsvpOptions} />
          ))}

          {dedicationText && (
            <p className="event-dedication" style={{ color: quouteColor, fontSize: `clamp(1rem, 2vw, ${Math.max(parseInt(quouteFontSize) - 5, 16)}px)` }}>
              {dedicationText}
            </p>
          )}

          {(groomParentsLabel || brideParentsLabel || dedicationSecond) && (
            <section className="family-section" style={{ color: parentsColor, "--parents-size": `${parentsFontSize}px` }}>
              <div className="family-section__line" aria-hidden="true" />
              <div className="family-groups">
                <div>
                  <strong>{groomParentsLabel}</strong>
                  <span>{groomParentsNames}</span>
                </div>
                <div>
                  <strong>{brideParentsLabel}</strong>
                  <span>{brideParentsNames}</span>
                </div>
              </div>
              {dedicationSecond && <p style={{ color: quouteColor }}>{dedicationSecond}</p>}
            </section>
          )}
        </div>
      </div>

      <footer className="invitation-footer">
        <a
          href="https://www.flashback.co.il/he/rsvps/?utm_source=invitations_system&utm_medium=guest_invitation&utm_campaign=rsvp_footer&utm_content=branded_card"
          target="_blank"
          rel="noopener noreferrer"
          title="אישורי הגעה לאירועים עם פלאשבק הפקות"
          aria-label="למידע על מערכת אישורי הגעה לאירועים של פלאשבק הפקות"
        >
          <img src="/assets/icons/ronenlogo.png" alt="פלאשבק הפקות - אישורי הגעה לאירועים" />
          <span className="invitation-footer__content">
            <small>מתכננים אירוע משלכם?</small>
            <span>מערכת חכמה לניהול אישורי הגעה וסידורי הושבה</span>
          </span>
          <span className="invitation-footer__action">
            <span>אישורי הגעה לאירוע - פלאשבק הפקות</span>
          </span>
          <span className="invitation-footer__party-icon" aria-hidden="true">🎉</span>
        </a>
      </footer>

      {!invitationOnly && <MobileRsvpCta />}

      {isInvitationExpanded && createPortal(
        <div
          className={`invitation-lightbox${isInvitationClosing ? " invitation-lightbox--closing" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={eventOwners || "תמונת ההזמנה המוגדלת"}
          onClick={() => setIsInvitationClosing(true)}
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget && isInvitationClosing) {
              setIsInvitationExpanded(false);
              setIsInvitationClosing(false);
            }
          }}
        >
          <div
            className="invitation-lightbox__content"
            style={expandedInvitationSize || undefined}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="invitation-lightbox__close"
              onClick={() => setIsInvitationClosing(true)}
              aria-label="סגירת התמונה המוגדלת"
              autoFocus
            >
              ×
            </button>
            <img
              src={attachmentUrl}
              alt={eventOwners || "הזמנה לאירוע"}
              onLoad={(event) => {
                const image = event.currentTarget;
                invitationNaturalSizeRef.current = {
                  width: image.naturalWidth,
                  height: image.naturalHeight,
                };
                setExpandedInvitationSize(getFittedInvitationSize(image.naturalWidth, image.naturalHeight));
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </main>
  );
};

export default Invitation;
